"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { ThemedLogo } from "@/components/ui/themed-logo";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { Moon, Search, Sun } from "@/components/icons";
import { usePrefersLight, setTheme } from "@/hooks/use-color-scheme";
import { navLinks, megaLinkProps } from "./data";
import { MorphChevron, MegaDropdown } from "./parts";
import { openSearchPalette } from "@/components/site/search/events";
import { APP_URL, FREE_TRIAL_LABEL, FREE_TRIAL_URL } from "@/lib/constants";
import { MAINTENANCE_PATH } from "@/lib/maintenance";
import { useMaintenanceMode } from "@/hooks/use-maintenance-mode";

function AnimatedHamburger({ open }: { open: boolean }) {
  const lineClass =
    "absolute left-1 right-1 h-0.5 bg-current rounded-full transition-transform duration-300 ease-in-out";
  return (
    <div className="relative h-6 w-6" aria-hidden="true">
      <span
        className={lineClass}
        style={{
          top: 7,
          transform: open ? "translateY(4px) rotate(45deg)" : "rotate(0)",
        }}
      />
      <span
        className={lineClass}
        style={{
          top: 15,
          transform: open ? "translateY(-4px) rotate(-45deg)" : "rotate(0)",
        }}
      />
    </div>
  );
}

const loginHref = APP_URL;

export default function Header() {
  const maintenanceMode = useMaintenanceMode();
  const prefersLight = usePrefersLight();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openMenu = (label: string) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenDropdown(label);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const showSurface = scrolled || mobileOpen;
  const trialHref = maintenanceMode ? MAINTENANCE_PATH : FREE_TRIAL_URL;

  return (
    <header
      className={`fixed top-0 right-0 left-0 z-[60] border-b border-(--header-border) bg-(--header-bg) backdrop-blur-md transition-all duration-300 ${showSurface ? "" : "md:bg-transparent md:backdrop-blur-none"}`}
      role="banner"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center" style={{ height: "var(--header-height)" }}>
          <Link
            href="/"
            aria-label="Proxidize — Home"
            className="mr-auto flex shrink-0 items-center"
          >
            <ThemedLogo width={116} height={26} priority />
          </Link>

          <nav className="hidden items-center gap-0.5 md:flex" aria-label="Primary navigation">
            {navLinks.map((link) =>
              link.mega ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => openMenu(link.label)}
                  onMouseLeave={scheduleClose}
                >
                  <button
                    className={`flex cursor-pointer items-center gap-[5px] rounded-md px-3 py-2 text-[13px] font-medium transition-colors ${openDropdown === link.label ? "text-foreground/90" : "text-foreground/55"}`}
                    aria-expanded={openDropdown === link.label}
                    aria-haspopup="true"
                  >
                    {link.label}
                    <MorphChevron open={openDropdown === link.label} />
                  </button>
                  {openDropdown === link.label && (
                    <MegaDropdown columns={link.mega} onClose={() => setOpenDropdown(null)} />
                  )}
                </div>
              ) : (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-foreground/55 hover:text-foreground/90 rounded-md px-3 py-2 text-[13px] font-medium transition-colors"
                >
                  {link.label}
                </Link>
              )
            )}

            <TooltipProvider delay={300} closeDelay={0}>
              <Tooltip>
                <TooltipTrigger
                  onClick={openSearchPalette}
                  aria-label="Search"
                  className="text-foreground/55 hover:text-foreground/90 ml-1 flex items-center gap-1.5 rounded-md px-2.5 py-2 text-[13px] font-medium transition-colors"
                >
                  <kbd className="border-foreground/12 flex size-5 items-center justify-center rounded-md border text-[11px] font-medium">
                    ⌘
                  </kbd>
                  <kbd className="border-foreground/12 flex size-5 items-center justify-center rounded-md border text-[11px] font-medium">
                    K
                  </kbd>
                  <Search className="size-4" aria-hidden="true" />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs font-semibold">
                  Search the site
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <ThemeToggle className="text-foreground/55 hover:text-foreground/90" />

            <span className="bg-foreground/12 mx-2 h-[18px] w-px" aria-hidden="true" />

            <Link
              href={loginHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground/55 hover:text-foreground/90 rounded-md px-3 py-2 text-[13px] font-medium transition-colors"
            >
              Log in
            </Link>

            <Link
              href={trialHref}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-primary-foreground ml-1 flex items-center rounded-[6px] px-3.5 py-[6px] text-[13px] font-medium transition-opacity select-none hover:opacity-90"
            >
              {FREE_TRIAL_LABEL}
            </Link>
          </nav>

          <Link
            href={loginHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground/78 mr-1 px-2 py-1.5 text-[13px] font-medium transition-colors md:hidden"
            onClick={() => setMobileOpen(false)}
          >
            Log in
          </Link>

          <Link
            href={trialHref}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary text-primary-foreground mr-2 flex items-center rounded-[6px] px-3 py-1.5 text-[12px] font-medium transition-opacity select-none hover:opacity-90 md:hidden"
            onClick={() => setMobileOpen(false)}
          >
            Start for free
          </Link>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              className="text-foreground rounded-md p-2 transition-colors md:hidden"
            >
              <AnimatedHamburger open={mobileOpen} />
            </SheetTrigger>
            <SheetContent
              side="right"
              showCloseButton={false}
              className="!bg-background !top-[var(--header-height)] !h-[calc(100dvh-var(--header-height))] !w-full !max-w-none overflow-y-auto !border-l-0 p-0"
            >
              <nav aria-label="Mobile navigation" className="flex flex-col px-4 py-4">
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    openSearchPalette();
                  }}
                  className="text-foreground/78 border-foreground/6 active:bg-foreground/6 flex items-center gap-3 rounded-md border-b px-2 py-4 text-left text-base font-medium transition-colors"
                >
                  <Search className="size-[18px]" aria-hidden="true" />
                  Search
                </button>

                <button
                  type="button"
                  onClick={() => setTheme(prefersLight ? "dark" : "light")}
                  className="text-foreground/78 border-foreground/6 active:bg-foreground/6 flex items-center gap-3 rounded-md border-b px-2 py-4 text-left text-base font-medium transition-colors"
                >
                  {prefersLight ? (
                    <Moon className="size-[18px]" aria-hidden="true" />
                  ) : (
                    <Sun className="size-[18px]" aria-hidden="true" />
                  )}
                  {prefersLight ? "Switch to dark theme" : "Switch to light theme"}
                </button>

                {navLinks.map((link) =>
                  link.mega ? (
                    <div key={link.label} className="border-foreground/6 border-b">
                      <p className="text-muted-foreground px-2 pt-5 pb-2 text-xs font-semibold tracking-widest uppercase">
                        {link.label}
                      </p>
                      <div className="flex flex-col pb-3">
                        {link.mega.flatMap((col) =>
                          col.items.map((item) => {
                            const itemContent = (
                              <span className="flex items-center gap-2">
                                {item.label}
                                {item.chip && (
                                  <span className="light:border-emerald-600/30 light:text-emerald-700 shrink-0 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] leading-none font-semibold text-emerald-400">
                                    {item.chip}
                                  </span>
                                )}
                              </span>
                            );
                            return (
                              <Link
                                key={item.label}
                                href={item.href}
                                {...megaLinkProps(item)}
                                className="text-foreground/78 active:bg-foreground/6 rounded-md px-4 py-3.5 text-base font-medium transition-colors"
                                onClick={() => setMobileOpen(false)}
                              >
                                {itemContent}
                              </Link>
                            );
                          })
                        )}
                      </div>
                    </div>
                  ) : (
                    <Link
                      key={link.label}
                      href={link.href}
                      className="text-foreground/78 border-foreground/6 active:bg-foreground/6 rounded-md border-b px-2 py-4 text-base font-medium transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      {link.label}
                    </Link>
                  )
                )}

                <Link
                  href={loginHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground/78 border-foreground/12 active:bg-foreground/6 mt-5 flex w-full items-center justify-center rounded-lg border px-4 py-3.5 text-base font-medium transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Log in
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
